import { useState } from 'react';
import gettext from '@sdk/lang/gettext';

import {
  SurveyCommentStepType,
  SurveyFinishStepType,
  SurveyStepType,
} from '@web/components/survey/types/';
import { isBadScore } from '@web/components/happinessSurvey/utils';
import SurveyScoreStep from '@web/components/survey/surveyScore/SurveyScoreStep';
import BookCall from '@web/components/survey/BookCall';
import SurveyStep from '@web/components/survey/SurveyStep';
import SurveyFinishStep from '@web/components/survey/SurveyFinishStep';
import SurveyCommentStep from '@web/components/survey/SurveyCommentStep';

export const BOOK_CALL_URL =
  'https://calendly.com/emily-worthington-sling/30-minute-customer-feedback-chat';

enum SurveySteps {
  rate = 1,
  comment = 2,
  finish = 3,
  finishCall = 4,
}

const STEPS_CONFIG = {
  [SurveySteps.rate]: {
    number: 1,
    subtitle: gettext('How would you rate your experience scheduling shifts on Sling?'),
    submitLabel: gettext('Next'),
  } as SurveyStepType,
  [SurveySteps.comment]: {
    text: gettext('Please tell us more'),
    number: 2,
  } as SurveyCommentStepType,
  [SurveySteps.finish]: {
    title: gettext('Thank you for your input!'),
    subtitle: gettext(
      'We highly value your feedback and use it to keep improving our product for you.'
    ),
  } as SurveyFinishStepType,
  [SurveySteps.finishCall]: {
    title: gettext('Thank you for your input!'),
    subtitle: gettext(
      'We highly value your feedback and use it to keep improving our product. We would love to hear more from you over a call.'
    ),
  } as SurveyFinishStepType,
};

const STEPS_COUNT = 2;

type Props = {
  score: number;
  setScore: (score: number) => void;
  comment: string;
  setComment: (comment: string) => void;
  submitForm: VoidFunction;
  closeSurvey: VoidFunction;
};

const HappinessSurveySteps = ({
  score,
  setScore,
  comment,
  setComment,
  submitForm,
  closeSurvey,
}: Props) => {
  const [step, setStep] = useState(SurveySteps.rate);

  const nextStep = () => {
    setStep((step) => {
      let next = step + 1;

      if (next === SurveySteps.finish && isBadScore(score)) {
        next = SurveySteps.finishCall;
      }

      if (next === SurveySteps.finish || next === SurveySteps.finishCall) {
        submitForm();
      }

      return next;
    });
  };

  if (step === SurveySteps.rate) {
    const currentStep = STEPS_CONFIG[SurveySteps.rate];
    return (
      <SurveyScoreStep
        score={score}
        onSetScore={setScore}
        submitScore={nextStep}
        text={currentStep.subtitle}
        submitLabel={currentStep.submitLabel}
      />
    );
  }

  if (step === SurveySteps.comment) {
    const currentStep = STEPS_CONFIG[SurveySteps.comment];
    return (
      <SurveyCommentStep
        isCommentRequired={false}
        comment={comment}
        setComment={setComment}
        submitStep={nextStep}
        stepsCount={STEPS_COUNT}
        stepNumber={currentStep.number}
        text={gettext(currentStep.text)}
        submitLabel={gettext(currentStep.submitLabel)}
      />
    );
  }

  if (step === SurveySteps.finish) {
    const currentStep = STEPS_CONFIG[SurveySteps.finish];
    return (
      <SurveyFinishStep
        title={gettext(currentStep.title)}
        subtitle={gettext(currentStep.subtitle)}
        closeSurvey={closeSurvey}
      />
    );
  }
  if (step === SurveySteps.finishCall) {
    return (
      <SurveyStep>
        <BookCall bookCallUrl={BOOK_CALL_URL} />
      </SurveyStep>
    );
  }

  return null;
};

export default HappinessSurveySteps;
